import SiteLayout from "@/components/feature/SiteLayout";
import ServicesHero from "./components/ServicesHero";
import ServiceDetailRow from "./components/ServiceDetailRow";
import FAQSection from "./components/FAQSection";
import CTASection from "@/pages/site-home/components/CTASection";
import { useServiceProducts } from "@/hooks/site/useProducts";

const ICON_BY_CATEGORY: Record<string, string> = {
  Residential: "ri-home-smile-line",
  Commercial: "ri-building-2-line",
  "Long Distance": "ri-roadster-line",
  Packing: "ri-archive-2-line",
  Storage: "ri-inbox-archive-line",
  Specialty: "ri-music-2-line",
};

const COLOR_BY_CATEGORY: Record<string, string> = {
  Residential: "from-brand-500 to-brand-700",
  Commercial: "from-ink-800 to-ink-900",
  "Long Distance": "from-brand-600 to-brand-800",
  Packing: "from-amber-500 to-orange-600",
  Storage: "from-emerald-600 to-emerald-800",
  Specialty: "from-rose-600 to-rose-800",
};

const TAGLINE_BY_CATEGORY: Record<string, string> = {
  Residential: "Apartments, condos & single-family homes",
  Commercial: "Offices, retail & light industrial",
  "Long Distance": "Cross-state & coast-to-coast",
  Packing: "Full-service or partial",
  Storage: "Short-term & long-term",
  Specialty: "Pianos, art, antiques, gun safes",
};

const BADGE_BY_CATEGORY: Record<string, string> = {
  "Long Distance": "Most popular",
  Specialty: "Specialty crew",
};

// Strip HTML to a short plain-text summary
function htmlToSummary(html: string | null, max = 220) {
  if (!html) return "";
  const text = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return text.length > max ? text.slice(0, max - 1) + "…" : text;
}

// Pull <li> bullets from the description for the features list
function extractFeatures(html: string | null): string[] {
  if (!html) return [];
  const matches = Array.from(html.matchAll(/<li>(.*?)<\/li>/gi));
  return matches.map((m) => m[1].replace(/<[^>]+>/g, "").trim()).filter(Boolean);
}

export default function SiteServicesPage() {
  const { products, loading } = useServiceProducts();

  return (
    <SiteLayout navVariant="solid">
      <ServicesHero />

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {loading && (
            <div className="py-32 flex items-center justify-center text-ink-500 text-sm">
              <i className="ri-loader-4-line animate-spin text-2xl text-brand-500 mr-2"></i>
              Loading services…
            </div>
          )}

          {!loading &&
            products.map((p, i) => {
              const categoryName = p.product_categories?.name ?? "Residential";
              const anchorId = categoryName.toLowerCase().replace(/\s+/g, "-");
              const image = p.media?.[0]?.url ?? "";
              return (
                <ServiceDetailRow
                  key={p.id}
                  id={anchorId}
                  productId={p.id}
                  icon={ICON_BY_CATEGORY[categoryName] ?? "ri-truck-line"}
                  title={p.name}
                  tagline={TAGLINE_BY_CATEGORY[categoryName] ?? categoryName}
                  description={htmlToSummary(p.description)}
                  features={extractFeatures(p.description).slice(0, 5)}
                  startingPrice={
                    p.discount_enabled && p.discount_price != null ? p.discount_price : p.price
                  }
                  color={COLOR_BY_CATEGORY[categoryName] ?? "from-brand-500 to-brand-700"}
                  imageUrl={image}
                  reverse={i % 2 === 1}
                  badge={BADGE_BY_CATEGORY[categoryName]}
                />
              );
            })}
        </div>
      </section>

      <FAQSection />
      <CTASection />
    </SiteLayout>
  );
}